import React, { useState, useEffect } from 'react';
import { 
  FileText, Download, Calendar, User, 
  Filter, Search, Building, DollarSign,
  AlertCircle, CheckCircle, Clock, FileCheck
} from 'lucide-react';
import { taxFormsService, agentsService } from '../../services';
import './TaxFormsManagement.css';

const TaxFormsManagement = () => {
  const [activeTab, setActiveTab] = useState('w9w8');
  const [taxForms, setTaxForms] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [show1099Modal, setShow1099Modal] = useState(false);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      if (activeTab === 'w9w8') {
        const [formsData, agentsData] = await Promise.all([
          taxFormsService.getAll(),
          agentsService.getAll()
        ]);
        setTaxForms(formsData);
        setAgents(agentsData);
      } else {
        // Load agents for 1099 generation
        const agentsData = await agentsService.getAll();
        setAgents(agentsData.filter(agent => agent.country === 'US'));
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate1099 = async (agentId) => {
    try {
      const result = await taxFormsService.generate1099(agentId, selectedYear);
      setSelectedAgent(result);
      setShow1099Modal(true);
    } catch (error) {
      console.error('Failed to generate 1099:', error);
      alert('Failed to generate 1099. Agent may not have submitted W-9.');
    }
  };

  const getFormStatusBadge = (agent) => {
    const hasW9 = taxForms.some(f => f.agent_id === agent.id && f.form_type === 'w9');
    const hasW8 = taxForms.some(f => f.agent_id === agent.id && f.form_type === 'w8ben');
    
    if (agent.country === 'US' && hasW9) {
      return <span className="status-badge status-complete">W-9 Complete</span>;
    } else if (agent.country !== 'US' && hasW8) {
      return <span className="status-badge status-complete">W-8BEN Complete</span>;
    } else {
      return <span className="status-badge status-pending">Form Required</span>;
    }
  };

  const filteredAgents = agents.filter(agent =>
    agent.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const currentYearForms = taxForms.filter(form => 
    new Date(form.created_at).getFullYear() === selectedYear
  );

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Tax Forms Management</h1>
        <p className="text-gray-600 mt-2">Manage agent tax forms and generate 1099s</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('w9w8')}
              className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'w9w8'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileText className="w-4 h-4 inline-block mr-2" />
              W-9 / W-8BEN Forms
            </button>
            <button
              onClick={() => setActiveTab('1099')}
              className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                activeTab === '1099'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <DollarSign className="w-4 h-4 inline-block mr-2" />
              1099 Forms
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'w9w8' ? (
            <>
              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-900">Total Agents</p>
                      <p className="text-2xl font-bold text-blue-600">{agents.length}</p>
                    </div>
                    <User className="w-8 h-8 text-blue-500" />
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-900">W-9 Forms</p>
                      <p className="text-2xl font-bold text-green-600">
                        {taxForms.filter(f => f.form_type === 'w9').length}
                      </p>
                    </div>
                    <FileCheck className="w-8 h-8 text-green-500" />
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-purple-900">W-8BEN Forms</p>
                      <p className="text-2xl font-bold text-purple-600">
                        {taxForms.filter(f => f.form_type === 'w8ben').length}
                      </p>
                    </div>
                    <FileCheck className="w-8 h-8 text-purple-500" />
                  </div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-yellow-900">Pending</p>
                      <p className="text-2xl font-bold text-yellow-600">
                        {agents.filter(a => !taxForms.some(f => f.agent_id === a.id)).length}
                      </p>
                    </div>
                    <Clock className="w-8 h-8 text-yellow-500" />
                  </div>
                </div>
              </div>

              {/* Search */}
              <div className="mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search agents..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Agents List */}
              <div className="overflow-x-auto">
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="loading-spinner"></div>
                  </div>
                ) : (
                  <table className="min-w-full tax-forms-table">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Country</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tax Form Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredAgents.map((agent) => {
                        const agentForms = taxForms.filter(f => f.agent_id === agent.id);
                        const latestForm = agentForms.sort((a, b) => b.version - a.version)[0];
                        
                        return (
                          <tr key={agent.id}>
                            <td className="px-6 py-4">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{agent.name}</p>
                                <p className="text-sm text-gray-500">{agent.email}</p>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900">
                              {agent.country || 'Not specified'}
                            </td>
                            <td className="px-6 py-4">
                              {getFormStatusBadge(agent)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {latestForm ? new Date(latestForm.created_at).toLocaleDateString() : '-'}
                            </td>
                            <td className="px-6 py-4">
                              {latestForm && (
                                <button
                                  onClick={() => {
                                    setSelectedAgent({ ...agent, form: latestForm });
                                    setShowFormModal(true);
                                  }}
                                  className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center"
                                >
                                  <FileText className="w-4 h-4 mr-1" />
                                  View Form
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          ) : (
            // 1099 Tab
            <>
              <div className="mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <label className="text-sm font-medium text-gray-700">Tax Year:</label>
                    <select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                      className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {[2024, 2023, 2022, 2021].map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* 1099 Eligible Agents */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-yellow-900">1099 Forms Information</p>
                    <p className="text-sm text-yellow-700 mt-1">
                      1099 forms are generated for US-based agents who have submitted W-9 forms and have received commission payments during the tax year.
                    </p>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="loading-spinner"></div>
                  </div>
                ) : (
                  <table className="min-w-full tax-forms-table">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">W-9 Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredAgents.map((agent) => {
                        const hasW9 = taxForms.some(f => f.agent_id === agent.id && f.form_type === 'w9');
                        
                        return (
                          <tr key={agent.id}>
                            <td className="px-6 py-4">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{agent.name}</p>
                                <p className="text-sm text-gray-500">{agent.email}</p>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              {hasW9 ? (
                                <span className="status-badge status-complete">W-9 Complete</span>
                              ) : (
                                <span className="status-badge status-pending">W-9 Required</span>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              {hasW9 ? (
                                <button
                                  onClick={() => handleGenerate1099(agent.id)}
                                  className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center"
                                >
                                  <FileText className="w-4 h-4 mr-1" />
                                  Generate 1099
                                </button>
                              ) : (
                                <span className="text-sm text-gray-500">W-9 Required</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* View Form Modal */}
      {showFormModal && selectedAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">
                {selectedAgent.form.form_type.toUpperCase()} Form - {selectedAgent.name}
              </h2>
            </div>
            <div className="p-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                  {JSON.stringify(JSON.parse(selectedAgent.form.form_data), null, 2)}
                </pre>
              </div>
              <div className="mt-4">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Digital Signature:</span> {selectedAgent.form.digital_signature}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Signed Date:</span> {new Date(selectedAgent.form.signature_date).toLocaleString()}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Version:</span> {selectedAgent.form.version}
                </p>
              </div>
            </div>
            <div className="p-6 border-t bg-gray-50 flex justify-end space-x-3">
              <button
                onClick={() => setShowFormModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 1099 Preview Modal */}
      {show1099Modal && selectedAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">1099-MISC Preview - Tax Year {selectedYear}</h2>
            </div>
            <div className="p-6">
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="font-medium text-gray-900 mb-4">Payer Information</h3>
                <p className="text-sm">MainStreet Travel Agency, Inc.</p>
                <p className="text-sm">929 W. Sunset Blvd. STE 21-292</p>
                <p className="text-sm">St. George, UT 84770</p>
                <p className="text-sm">TIN: 87-0968282</p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="font-medium text-gray-900 mb-4">Recipient Information</h3>
                <p className="text-sm"><span className="font-medium">Name:</span> {selectedAgent.w9_data?.name}</p>
                <p className="text-sm"><span className="font-medium">TIN:</span> {selectedAgent.w9_data?.tin}</p>
                <p className="text-sm"><span className="font-medium">Address:</span> {selectedAgent.w9_data?.address}</p>
              </div>

              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="font-medium text-gray-900 mb-4">Income Information</h3>
                <p className="text-lg">
                  <span className="font-medium">Box 3 - Other Income:</span> 
                  <span className="ml-2 text-2xl font-bold">${selectedAgent.total_commission?.toLocaleString() || '0.00'}</span>
                </p>
              </div>
            </div>
            <div className="p-6 border-t bg-gray-50 flex justify-end space-x-3">
              <button
                onClick={() => setShow1099Modal(false)}
                className="px-4 py-2 text-gray-700 bg-white border rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
              >
                <Download className="w-4 h-4 mr-2" />
                Download 1099
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxFormsManagement;
